import { observer } from "mobx-react";
import { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { PieChartTooltip } from "./PieChartToolTip";
import { CustomLabel } from "./CustomLabel";
import { PieChartItem, pieChartProps } from "./shared";
import { Text } from "@chakra-ui/react";
import { t } from "i18next";
import {
  mapGradient,
  mapGradientStep,
  notCodedColor,
} from "../../../../map/utils/map-utils";
import { useStores } from "../../../../shared/stores/RootStore";
import {
  HlaAttributeName,
  hlaAttributeOptions,
} from "../../../../map/models/HlaAttribute";
import {
  coloringSteps,
  getHlaAttributeValueForColoring,
} from "../../../../map/models/HlaColoring";

const HlaAttributePieChartBase = ({
  attributeName,
}: {
  attributeName: HlaAttributeName;
}) => {
  const { filterStore } = useStores();
  const ways = filterStore.filteredHlaWays;

  const data = useMemo(() => {
    const totalWays = ways.length;
    if (!totalWays) return;

    const buildSelectData = () => {
      const lengthCounter = new Map<string, number>();
      const notCodedKey = "";

      const options = hlaAttributeOptions[attributeName]!;

      // populate dataMap
      options.forEach((option) => lengthCounter.set(`${option.value}`, 0));
      lengthCounter.set(notCodedKey, 0);

      ways.forEach((way) => {
        [way.frontFacingLane, way.backFacingLane]
          .filter((lane) => !!lane)
          .forEach((lane) => {
            if (lane && filterStore.filterHlaLane(lane)) {
              const value = getHlaAttributeValueForColoring(
                attributeName,
                way,
                lane,
              );
              const label = value != null ? value.toString() : notCodedKey;

              incrMapValue(lengthCounter, label, way.lengthM);
            }
          });
      });

      const data: PieChartItem[] = [];
      for (const [value, lengthM] of lengthCounter) {
        const optionIndex = options.findIndex(
          (option) => `${option.value}` === value,
        );

        if (lengthM === 0) continue;

        data.push({
          name: value === notCodedKey ? t(`coding:null`) : value,
          value: lengthM / 1000,
          color:
            value === notCodedKey
              ? notCodedColor
              : mapGradientStep(options.length, optionIndex),
        });
      }

      return data;
    };

    const buildNumberData = (steps: number[]) => {
      const dataMap = new Map<number, number>();

      const notCodedIndex = -1;

      dataMap.set(notCodedIndex, 0);

      steps.forEach((step) => {
        dataMap.set(step, 0);
      });

      ways.forEach((way) => {
        [way.frontFacingLane, way.backFacingLane]
          .filter((lane) => !!lane)
          .forEach((lane) => {
            if (lane && filterStore.filterHlaLane(lane)) {
              const value = getHlaAttributeValueForColoring(
                attributeName,
                way,
                lane,
              );

              if (value === null) {
                incrMapValue(dataMap, notCodedIndex, way.lengthM);
                return;
              }

              const step = steps.find((_, index) => {
                const nextStep = steps[index + 1];
                if (nextStep === undefined) return true;

                if (value < nextStep) return true;

                return false;
              })!;

              incrMapValue(dataMap, step, way.lengthM);
            }
          });
      });

      const data: PieChartItem[] = [];
      for (const [value, lengthM] of dataMap) {
        if (lengthM === 0) continue;

        const stepIndex = steps.findIndex((step) => step === value);
        const nextStep = steps[stepIndex + 1];

        data.push({
          name:
            value === notCodedIndex
              ? t(`coding:null`)
              : nextStep
              ? `${value} to ${nextStep}`
              : `> ${value}`,
          value: lengthM / 1000,
          color:
            value === notCodedIndex
              ? notCodedColor
              : `#${mapGradient
                  .hsvAt(value / steps[steps.length - 1])
                  .toHex()}`,
        });
      }

      return data;
    };

    switch (attributeName) {
      case HlaAttributeName.AverageDailyTraffic:
      case HlaAttributeName.AverageRoadSpeed:
        return buildNumberData(coloringSteps[attributeName]!);
      default:
        return buildSelectData();
    }

    // eslint-disable-next-line
  }, [ways, attributeName]);

  if (!data)
    return (
      <Text fontSize="xs" alignSelf={"start"}>
        {t(`map:pieChart.noData`)}
      </Text>
    );

  return (
    <PieChart {...pieChartProps}>
      <Tooltip content={<PieChartTooltip />} />

      <Pie
        nameKey="name"
        dataKey="value"
        label={CustomLabel}
        data={data}
        outerRadius={80}
      >
        {data.map((item, i) => (
          <Cell key={i} fill={item.color} />
        ))}
      </Pie>
    </PieChart>
  );
};

export const HlaAttributePieChart = observer(HlaAttributePieChartBase);

const incrMapValue = (map: Map<any, number>, key: any, lengthM: number) =>
  map.set(key, map.get(key)! + lengthM);
